import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { PricingVariant } from '../../../api/pricingVariants';
import { CloneFormData } from '../types';

interface ClonePlansDialogProps {
  isDark: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  variant: PricingVariant | null;
  variants: PricingVariant[];
  formData: CloneFormData;
  onFormDataChange: React.Dispatch<React.SetStateAction<CloneFormData>>;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
}

const BASE = 'base';

export function ClonePlansDialog({
  isDark,
  isOpen,
  onOpenChange,
  variant,
  variants,
  formData,
  onFormDataChange,
  onSubmit,
  isSubmitting,
}: ClonePlansDialogProps) {
  const inputClass = cn(
    isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200',
  );
  const labelClass = cn(isDark ? 'text-slate-300' : 'text-slate-700');

  const sources = variants.filter((v) => v.id !== variant?.id && (v.plans?.length ?? 0) > 0);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-lg', isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white')}>
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#111111]')}>
            Скопировать тарифы в «{variant?.name}»
          </DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
            Копия прайс-листа с пересчитанными ценами. Цены потом можно поправить
            поштучно на странице «Тарифы».
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className={labelClass}>Источник</Label>
            <Select
              value={formData.from_variant_id || BASE}
              onValueChange={(value) =>
                onFormDataChange((prev) => ({
                  ...prev,
                  from_variant_id: value === BASE ? undefined : value,
                }))
              }
            >
              <SelectTrigger className={inputClass}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={BASE}>Базовый прайс-лист (текущие цены)</SelectItem>
                {sources.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>Суффикс кода *</Label>
            <Input
              value={formData.code_suffix}
              onChange={(e) =>
                onFormDataChange((prev) => ({ ...prev, code_suffix: e.target.value.toUpperCase() }))
              }
              placeholder="_CHEAP"
              className={inputClass}
            />
            <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
              Коды тарифов уникальны на всю базу: PREMIUM_1M + _CHEAP → PREMIUM_1M_CHEAP.
            </p>
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>Множитель цены</Label>
            <Input
              type="number"
              step="0.05"
              min={0.01}
              value={formData.price_multiplier}
              onChange={(e) =>
                onFormDataChange((prev) => ({
                  ...prev,
                  price_multiplier: parseFloat(e.target.value) || 1,
                }))
              }
              className={inputClass}
            />
            <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
              0.4 — вдвое с лишним дешевле, 2 — вдвое дороже. Разброс должен быть
              кратным: на выборке в сотни человек 10% разницы утонут в шуме.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isSubmitting}
            className="bg-[#C9A227] hover:bg-[#C9A227]/90"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Скопировать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
